import { Field, Form, Formik } from 'formik';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'context/AuthContext.jsx';
import { Button, Input } from 'components/common';
import { DEFAULT_ROUTE } from 'components/layout/navigation.js';
import { loginInitialValues, loginSchema } from 'schema/login.mjs';

/**
 * The sign-in screen.
 *
 * Formik's component API owns the field state and submission; Yup owns the rules
 * (schema/login.mjs, which is deliberately minimal — see the note there about
 * why there is no password-length rule).
 *
 * Three things worth keeping in mind if this is edited:
 *
 *  - <Field> is used with a RENDER PROP rather than `as={Input}`. `as` forwards
 *    the field props but not `meta`, so there would be no way to hand the error
 *    to Input — and Input is what wires aria-invalid / aria-describedby to the
 *    message. Rendering the error through <ErrorMessage> instead would put it
 *    outside Input and break that link, so the message would be red text a
 *    screen reader never associates with the box it belongs to.
 *
 *  - A wrong password is a form-level status, NOT a field error. The server does
 *    not say which of the two was wrong, so blaming one field would be a guess —
 *    wrong half the time, and a small credential-enumeration hint the rest.
 *
 *  - Field errors appear on blur or on submit, never while someone is still
 *    typing their first character. Validation on change stays on so an error
 *    CLEARS as soon as it is fixed; `meta.touched` gates whether it is shown.
 */
export default function LoginPage() {
  const { login, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const organization = new URLSearchParams(location.search || location.state?.from?.search || '').get('organization') || undefined;

  const target = location.state?.from?.pathname ?? DEFAULT_ROUTE;
  if (isSignedIn && !organization) return <Navigate to={target} replace />;

  const onSubmit = async (values, { setStatus }) => {
    setStatus(undefined);
    const res = await login(values.username.trim(), values.password, organization);
    if (res.ok) navigate(res.role === 'super_admin' ? '/' : target, { replace: true });
    else setStatus(res.error);
  };

  return (
    <div id='login-page'>
      <Formik initialValues={loginInitialValues} validationSchema={loginSchema} onSubmit={onSubmit}>
        {({ isSubmitting, status }) => (
          <Form className='login-box' noValidate>
            <div className='login-logo'>⚡</div>
            <h1>Affiliate Dashboard</h1>
            <p>Sign in to your dashboard or organization management</p>

            <Field name='username'>
              {({ field, meta }) => (
                <Input
                  {...field}
                  label='Username'
                  id='login-user'
                  autoComplete='username'
                  placeholder='Enter username'
                  error={meta.touched && meta.error ? meta.error : undefined}
                  disabled={isSubmitting}
                />
              )}
            </Field>
            <br />
            <Field name='password'>
              {({ field, meta }) => (
                <Input
                  {...field}
                  label='Password'
                  id='login-pass'
                  type='password'
                  autoComplete='current-password'
                  placeholder='••••••••'
                  error={meta.touched && meta.error ? meta.error : undefined}
                  disabled={isSubmitting}
                />
              )}
            </Field>

            <br />
            <br />

            {/* Whatever the server said — kept separate from the field errors,
                since it is about the pair, not about one box. */}
            {status && (
              <div className='error login-error' role='alert'>
                {status}
              </div>
            )}

            <Button type='submit' full disabled={isSubmitting}>
              {isSubmitting ? 'Signing in…' : 'Sign In'}
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
}
